package com.snakesworld.game.ui

import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.material3.ripple
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.snakesworld.game.network.AuthState
import com.snakesworld.game.ui.theme.LocalGameCanvasColors
import com.snakesworld.game.ui.theme.appFontFamily
import com.snakesworld.game.viewmodel.AuthViewModel
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import snakesworld.shared.generated.resources.Res
import snakesworld.shared.generated.resources.app_icon
import snakesworld.shared.generated.resources.app_name
import snakesworld.shared.generated.resources.connecting_error
import snakesworld.shared.generated.resources.connecting_handshake
import snakesworld.shared.generated.resources.connecting_title
import snakesworld.shared.generated.resources.offline_splash_hint
import snakesworld.shared.generated.resources.play_offline
import snakesworld.shared.generated.resources.retry_connection
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin
import kotlin.time.Duration.Companion.milliseconds

@Composable
fun SplashScreen(
    viewModel: AuthViewModel,
    onAuthenticated: () -> Unit,
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val canvasColors = LocalGameCanvasColors.current
    val infiniteTransition = rememberInfiniteTransition(label = "splash")

    val pulseScale by infiniteTransition.animateFloat(
        initialValue = 0.95f,
        targetValue = 1.05f,
        animationSpec = infiniteRepeatable(
            animation = tween(800, easing = FastOutSlowInEasing),
            repeatMode = RepeatMode.Reverse
        ),
        label = "pulse"
    )

    val orbitRotation by infiniteTransition.animateFloat(
        initialValue = 0f,
        targetValue = 360f,
        animationSpec = infiniteRepeatable(
            animation = tween(4000, easing = FastOutSlowInEasing),
            repeatMode = RepeatMode.Restart
        ),
        label = "orbit"
    )

    // Trigger anonymous authentication if user is unauthenticated (e.g. initial launch or after data clearance / sign out)
    androidx.compose.runtime.LaunchedEffect(Unit) {
        if (uiState.authState !is AuthState.Authenticated && uiState.authState !is AuthState.Initializing) {
            viewModel.initializeAndSignIn()
        }
    }

    // Authenticated guests and offline Single Player both leave the splash screen.
    androidx.compose.runtime.LaunchedEffect(uiState.isReadyToPlay) {
        if (uiState.isReadyToPlay) {
            kotlinx.coroutines.delay(400.milliseconds)
            onAuthenticated()
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(canvasColors.backgroundColor),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            // Neon Snake Center Logo with Orbiting Pellets
            Box(
                modifier = Modifier.size(160.dp),
                contentAlignment = Alignment.Center
            ) {
                Canvas(modifier = Modifier.fillMaxSize()) {
                    val center = Offset(size.width / 2f, size.height / 2f)
                    val baseRadius = 55f * pulseScale

                    // Outer glowing neon ring
                    drawCircle(
                        color = canvasColors.boundaryColor.copy(alpha = 0.35f),
                        radius = baseRadius + 14f
                    )
                    drawCircle(
                        color = canvasColors.boundaryColor,
                        radius = baseRadius,
                        style = androidx.compose.ui.graphics.drawscope.Stroke(width = 5f)
                    )

                    // Orbiting neon pellets
                    val pelletColors = listOf(Color(0xFFFF007F), Color(0xFF00FF66), Color(0xFFFFEA00), Color(0xFF00F0FF))
                    for (i in 0..3) {
                        val angle = (orbitRotation + i * 90f) * (PI.toFloat() / 180f)
                        val orbitDist = baseRadius + 22f
                        val pelletPos = Offset(
                            center.x + orbitDist * cos(angle),
                            center.y + orbitDist * sin(angle)
                        )
                        drawRect(
                            color = pelletColors[i],
                            topLeft = Offset(pelletPos.x - 5f, pelletPos.y - 5f),
                            size = Size(10f, 10f)
                        )
                    }
                }

                Image(
                    painter = painterResource(Res.drawable.app_icon),
                    contentDescription = stringResource(Res.string.app_name),
                    modifier = Modifier.size(88.dp),
                    contentScale = ContentScale.Fit,
                )
            }

            Spacer(modifier = Modifier.height(24.dp))

            // Game Title + app icon
            AppBrandTitle(
                fontSize = 36.sp,
                letterSpacing = 4.sp,
                iconSize = 40.dp,
                canvasColors = canvasColors,
            )


            Spacer(modifier = Modifier.height(56.dp))

            // Auth State Gate: Automatically navigates when authenticated
            when (val authState = uiState.authState) {
                is AuthState.Authenticated -> {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        modifier = Modifier
                            .border(1.dp, canvasColors.accentColor.copy(alpha = 0.5f), RoundedCornerShape(6.dp))
                            .background(canvasColors.hudBgColor.copy(alpha = 0.8f), RoundedCornerShape(6.dp))
                            .padding(horizontal = 20.dp, vertical = 12.dp)
                    ) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(16.dp),
                            color = canvasColors.accentColor,
                            strokeWidth = 2.dp
                        )
                        Text(
                            text = stringResource(Res.string.connecting_title),
                            color = canvasColors.accentColor,
                            fontSize = 12.sp,
                            fontFamily = appFontFamily(),
                            fontWeight = FontWeight.Bold,
                            letterSpacing = 1.sp
                        )
                    }
                }
                is AuthState.Error -> {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        modifier = Modifier.padding(horizontal = 32.dp)
                    ) {
                        Text(
                            text = stringResource(Res.string.connecting_error),
                            color = canvasColors.dangerColor,
                            fontSize = 13.sp,
                            fontWeight = FontWeight.Black,
                            fontFamily = appFontFamily(),
                            textAlign = TextAlign.Center
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = authState.message,
                            color = canvasColors.hudTextColor.copy(alpha = 0.75f),
                            fontSize = 11.sp,
                            fontFamily = appFontFamily(),
                            textAlign = TextAlign.Center
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Text(
                            text = stringResource(Res.string.offline_splash_hint),
                            color = canvasColors.hudTextColor.copy(alpha = 0.85f),
                            fontSize = 11.sp,
                            fontFamily = appFontFamily(),
                            textAlign = TextAlign.Center
                        )
                        Spacer(modifier = Modifier.height(16.dp))
                        val retryShape = RoundedCornerShape(6.dp)
                        Box(
                            modifier = Modifier
                                .clip(retryShape)
                                .background(canvasColors.hudBgColor, retryShape)
                                .border(1.dp, canvasColors.hudTextColor, retryShape)
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = ripple(bounded = true, color = canvasColors.hudTextColor),
                                    onClick = { viewModel.initializeAndSignIn() }
                                )
                                .padding(horizontal = 24.dp, vertical = 10.dp)
                        ) {
                            Text(
                                text = stringResource(Res.string.retry_connection),
                                color = canvasColors.hudTextColor,
                                fontSize = 13.sp,
                                fontWeight = FontWeight.Bold,
                                fontFamily = appFontFamily()
                            )
                        }
                        Spacer(modifier = Modifier.height(10.dp))
                        Box(
                            modifier = Modifier
                                .clip(retryShape)
                                .background(canvasColors.accentColor, retryShape)
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = ripple(bounded = true, color = canvasColors.hudBgColor),
                                    onClick = { viewModel.continueOffline() }
                                )
                                .padding(horizontal = 24.dp, vertical = 10.dp)
                        ) {
                            Text(
                                text = stringResource(Res.string.play_offline),
                                color = canvasColors.hudBgColor,
                                fontSize = 13.sp,
                                fontWeight = FontWeight.Bold,
                                fontFamily = appFontFamily()
                            )
                        }
                    }
                }
                AuthState.Initializing, AuthState.Uninitialized -> {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        modifier = Modifier
                            .border(1.dp, canvasColors.hudTextColor.copy(alpha = 0.25f), RoundedCornerShape(6.dp))
                            .background(canvasColors.hudBgColor.copy(alpha = 0.6f), RoundedCornerShape(6.dp))
                            .padding(horizontal = 20.dp, vertical = 12.dp)
                    ) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(16.dp),
                            color = canvasColors.accentColor,
                            strokeWidth = 2.dp
                        )
                        Text(
                            text = stringResource(Res.string.connecting_handshake),
                            color = canvasColors.hudTextColor.copy(alpha = 0.85f),
                            fontSize = 12.sp,
                            fontFamily = appFontFamily(),
                            fontWeight = FontWeight.Bold,
                            letterSpacing = 1.sp
                        )
                    }
                }
            }
        }
    }
}
